import type { MouseEvent, SyntheticEvent } from "react";
import type { components } from "../../api/types";
import {
  STATE_WORD,
  rollupState,
  type AreaGroupData,
  type SpecRow,
  type SpecRowState,
} from "../../lib/specLines";
import { Row } from "./Row";
import { StateChip } from "./StateChip";

type Node = components["schemas"]["Node"];

// ---- What the column's heads share (0051 slice 3) --------------------------

// A head's row: on a phone a 44 px tap target.
export const HEAD_CLASS = "flex min-h-11 sm:min-h-0 items-center gap-2 py-1.5";
// The same row as a fold's summary: the column draws its own caret.
export const SUMMARY_CLASS = `${HEAD_CLASS} cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden`;

// foldProps: the column holds each fold's open state. A click on the head
// toggles it (never the browser's own toggle, so a test and every browser
// agree); a fold the browser opens by itself (find in page) tells the column.
export function foldProps(open: boolean, onToggle: (open: boolean) => void) {
  return {
    details: {
      open,
      onToggle: (e: SyntheticEvent<HTMLDetailsElement>) => {
        if (e.currentTarget.open !== open) onToggle(e.currentTarget.open);
      },
    },
    summary: {
      onClick: (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        onToggle(!open);
      },
    },
  };
}

// Caret: a fold's open mark, drawn (no text). A head with nothing to open keeps
// its place with a blank one.
export function Caret({ open }: { open?: boolean }) {
  if (open === undefined) return <span aria-hidden="true" className="inline-block h-1.5 w-1.5 shrink-0" />;
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-1.5 w-1.5 shrink-0 border-b border-r border-ink-400 transition-transform ${open ? "rotate-45" : "-rotate-45"}`}
    />
  );
}

// headTally is what a layer's or an area's head reads over its passing rows
// (the rows the active filter keeps): their one rollup chip and how many of
// them read that chip's word, never how many rows the head holds (RULED
// 2026-10-15: "BLOCKER 8" is eight blockers, where a total read "BLOCKER 101"
// as 101 blockers). The count goes by the word the founder reads, so the two
// states that read "Saved" count together. Null when no row passes: no chip,
// no number.
export function headTally(rows: readonly SpecRow[]): { state: SpecRowState; count: number } | null {
  const state = rollupState(rows.map((r) => r.state));
  if (state === null) return null;
  const word = STATE_WORD[state];
  return { state, count: rows.filter((r) => STATE_WORD[r.state] === word).length };
}

// HeadTally: a head's chip and its number (headTally); the number is bare,
// read aloud with the chip's word as the chip spells it ("8 Blocker": never a
// lower-case "proposed", which reads as the raw status).
export function HeadTally({ rows }: { rows: readonly SpecRow[] }) {
  const tally = headTally(rows);
  if (tally === null) return null;
  return (
    <span className="ml-auto flex shrink-0 items-center gap-2">
      <StateChip state={tally.state} />
      <span aria-hidden="true" data-testid="head-count" className="font-mono text-[11px] tabular-nums text-ink-400">
        {tally.count}
      </span>
      <span className="sr-only">{`${tally.count} ${STATE_WORD[tally.state]}`}</span>
    </span>
  );
}

// ---- An area -------------------------------------------------------------------

// One area of a layer: closed unless the column holds it open; its head reads
// the area's name, its rollup chip and how many rows read it; its rows on
// expand.
export function AreaGroup({
  ring,
  area,
  open,
  onToggle,
  nodeById,
  onOpenRow,
  registerRow,
}: {
  ring: number;
  area: AreaGroupData;
  open: boolean;
  onToggle: (open: boolean) => void;
  nodeById: ReadonlyMap<string, Node>;
  onOpenRow: (id: string) => void;
  registerRow: (id: string, el: HTMLButtonElement | null) => void;
}) {
  const fold = foldProps(open, onToggle);
  return (
    <details data-testid={`area-${ring}-${area.id}`} {...fold.details} className="min-w-0">
      <summary {...fold.summary} className={SUMMARY_CLASS}>
        <Caret open={open} />
        <span className="min-w-0 flex-1 break-words text-[14px] text-ink-200">{area.name}</span>
        <HeadTally rows={area.rows} />
      </summary>
      <div className="space-y-0.5 pb-2 pl-4">
        {area.rows.map((row) => (
          <Row key={row.node.id} row={row} nodeById={nodeById} onOpen={onOpenRow} register={registerRow} />
        ))}
      </div>
    </details>
  );
}
